import {
  Autocomplete,
  ClickAwayListener,
  Box,
  Grid,
  Checkbox,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  FormControlLabel,
  MenuItem,
  Typography,
  FormControl,
  CircularProgress,
  Button,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import _unionBy from 'lodash/unionBy';
import _without from 'lodash/without';
import React, { useCallback, useEffect, useState, useReducer } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import Page from 'common/shared-ui/src/components/Page';
import PlusIcon from 'common/shared-ui/src/icons/PlusIcon';
import RadioButton from 'common/shared-ui/src/icons/RadioButton';
import RadioButtonSelected from 'common/shared-ui/src/icons/RadioButtonSelected';
import ReportDeleteIcon from 'common/shared-ui/src/icons/ReportDeleteIcon';
import { generateRedirectUrl } from 'common/utils/getRedirectUrl';
import { getTranslatedText } from 'common/utils/getTranslatedText';

import {
  getProjectReports,
  updateProjectReports,
} from '../../../../packages/common/api';
import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

// Original class: tableCell
const StyledTableCell = styled(TableCell)(theme => ({
  '& .MuiInputBase-root': {
    background: 'none !important',
  },
}));
// Original class: radioButtonsGroupWrap
const StyledRadioButtonsGroupWrap = styled(Box)(theme => ({
  padding: '10px 18px',
}));
// Original class: radioButtonWrap
const StyledRadioButtonWrap = styled(Box)(theme => ({
  width: '100%',
}));
// Original class: radioButtonTxt
const StyledRadioButtonTxt = styled(Box)(theme => ({
  fontSize: '12px !important',
  fontWeight: '400 !important',
  lineHeight: '20px !important',
  marginLeft: '10px',
}));
// Original class: menuGroupWrap
const StyledMenuGroupWrap = styled(Box)(theme => ({
  borderTop: '1px solid #E4E4EF',
}));
// Original class: saveReportButton
const StyledSaveReportButton = styled(Button)(theme => ({
  marginRight: '10px !important',
  borderRadius: '16px !important',

  '& .MuiInputBase-root': {
    '&.Mui-disabled': {
      backgroundColor: '#DCDFF4 !important',
    },
  },

  '&.Mui-disabled': {
    backgroundColor: '#DCDFF4 !important',
    color: '#656985 !important',
  },
}));
// Original class: cancelReportButton
const StyledCancelReportButton = styled(Button)(theme => ({
  marginRight: '10px !important',
  borderRadius: '16px !important',

  '&.Mui-disabled': {
    backgroundColor: '#DCDFF4 !important',
    color: '#656985 !important',
  },
}));
// Original class: settingsWrap
const StyledSettingsWrap = styled(Grid)(theme => ({
  width: '100%',
  backgroundColor: '#FFF',
  borderRadius: '16px',
  padding: '19px 64px 17px 19px !important',
}));
// Original class: autocompleteRolesList
const StyledAutocompleteRolesList = styled(Autocomplete)(theme => ({
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
  },

  '& .MuiAutocomplete-root': {
    width: '100% !important',
  },

  '& .MuiOutlinedInput-root': {
    height: 'auto !important',
  },
}));
// Original class: flexBox
const StyledFlexBox = styled(Box)(theme => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '10px',
}));
// Original class: allBox
const StyledAllBox = styled(Box)(theme => ({
  width: '70%',
}));
// Original class: txtField
const StyledTxtField = styled(TextField)(theme => ({
  width: '288px',
}));
// Original class: customPopper
const StyledCustomPopper = styled(Popper)(theme => ({
  zIndex: '2000 !important',
}));
// Original class: subItemChip
const StyledSubItemChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    background: 'none',
    '& .MuiChip-label': {
      paddingLeft: '0px',
      paddingRight: '3px',
    },
    '& .MuiChip-deleteIcon': {
      display: 'none',
    },
  },
}));
const ProjectReports = () => {
  const getLocalString = useTranslation();
  const redmineToken = useSelector(state => state.user.redmine_access_token);
  const [isLoading, setIsLoading] = useState(true);
  const [changes, setChanges] = useState(false);
  const [reports, setReports] = React.useState([]);
  const [roles, setRoles] = React.useState([]);
  const pageTitle = getLocalString('AdminReports', 'title');
  const isUrlValid = url =>
    url.length < 2 || !url.includes('.') || !url.startsWith('http');
  const addReport = () => {
    setChanges(true);
    const newReport = {
      name: '',
      url: '',
      isForAll: false,
      roles: JSON.parse(JSON.stringify(roles)),
    };
    let tempReports = [...reports];
    setReports(current => [...current, newReport]);
  };
  const deleteReport = (report, index) => {
    let tempReports = JSON.parse(JSON.stringify(reports));
    tempReports[index].isDeleted = true;
    setChanges(true);
    setReports(tempReports);
  };
  const handleCancelReports = async () => {
    try {
      const response = await getProjectReports();
      setReports(response.reports);
      setRoles(response.roles);
      setIsLoading(false);
      setChanges(false);
    } catch (e) {
      console.error(e);
    }
  };
  const PaperComponent = (props, index) => {
    let radioButtonAll = getLocalString('labels', 'allUsers');
    let radioButtonCustom = getLocalString('labels', 'thisRoles');
    return (
      <Paper {...props}>
        <StyledRadioButtonsGroupWrap>
          <StyledRadioButtonWrap>
            <IconButton
              disableRipple
              onMouseDown={() => {
                let tempReports = [...reports];
                tempReports[index].isForAll = true;
                setReports(tempReports);
                setChanges(true);
              }}
            >
              {reports[index].isForAll ? (
                <RadioButtonSelected
                  width={14}
                  height={14}
                  viewBox='0 0 14 14'
                />
              ) : (
                <RadioButton width={14} height={14} viewBox='0 0 14 14' />
              )}
              <StyledRadioButtonTxt>{radioButtonAll}</StyledRadioButtonTxt>
            </IconButton>
          </StyledRadioButtonWrap>
          <StyledRadioButtonWrap>
            <IconButton
              disableRipple
              onMouseDown={() => {
                let tempReports = [...reports];
                tempReports[index].isForAll = false;
                setReports(tempReports);
                setChanges(true);
              }}
            >
              {!reports[index].isForAll ? (
                <RadioButtonSelected
                  width={14}
                  height={14}
                  viewBox='0 0 14 14'
                />
              ) : (
                <RadioButton width={14} height={14} viewBox='0 0 14 14' />
              )}
              <StyledRadioButtonTxt>{radioButtonCustom}</StyledRadioButtonTxt>
            </IconButton>
          </StyledRadioButtonWrap>
        </StyledRadioButtonsGroupWrap>
        <StyledMenuGroupWrap>
          {!reports[index].isForAll ? <>{props.children}</> : <></>}
        </StyledMenuGroupWrap>
      </Paper>
    );
  };
  const handleChangeInput = (event, type, index) => {
    setChanges(true);
    let tempReports = [...reports];
    tempReports[index][type] = event.target.value;
    setReports(tempReports);
  };
  const handleChangeSelect = (event, role, index) => {
    setChanges(true);
    let tempReports = [...reports];
    let reportIndex = tempReports[index].roles.findIndex(
      obj => obj.roleId == event.target.value,
    );
    tempReports[index].roles[reportIndex].isChecked =
      tempReports[index].roles[reportIndex].isChecked === true ? false : true;
    setReports(tempReports);
  };
  const handleSaveReports = async () => {
    setIsLoading(true);
    let tempReports = JSON.parse(JSON.stringify(reports));
    tempReports = tempReports.map((report, index) => {
      report.roles = report.roles.filter(filter => filter.isChecked);
      report.roles = report.roles.map((role, index) => {
        return role.roleId;
      });
      return report;
    });
    let data = {
      reports: tempReports,
    };
    try {
      const response = await updateProjectReports(data);
      setReports(response.data.reports);
      setIsLoading(false);
    } catch (error) {
      return console.log(error);
    }
  };
  const closePoper = async (e, index) => {
    console.log(e.target.value);
  };
  useEffect(() => {
    async function fetchProjectReports() {
      try {
        const response = await getProjectReports();
        setReports(response.reports);
        setRoles(response.roles);
        setIsLoading(false);
      } catch (e) {
        console.error(e);
      }
    }
    fetchProjectReports();
  }, []);
  return (
    <Page title={pageTitle} width='100%'>
      <Grid
        container
        spacing={5}
        sx={{
          marginTop: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
          display: 'flex',
          position: 'relative',
        }}
      >
        <Typography variant='h1'>{pageTitle}</Typography>
        <Box>
          <StyledSaveReportButton
            disabled={!changes}
            onClick={handleSaveReports}
            variant='cutBluePrimary'
          >
            {getLocalString('buttons', 'save')}
          </StyledSaveReportButton>
          <StyledCancelReportButton
            disabled={!changes}
            onClick={handleCancelReports}
            variant='cutBluePrimary'
          >
            {getLocalString('buttons', 'cancel')}
          </StyledCancelReportButton>
        </Box>
      </Grid>
      <Grid
        container
        spacing={5}
        sx={{
          marginTop: 5,
          position: 'relative',
        }}
      >
        {!isLoading ? (
          <StyledSettingsWrap item xs={11}>
            <TableContainer>
              <Table>
                <TableHead align='left'>
                  <TableRow>
                    <TableCell>
                      <Typography
                        variant='h4'
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {getLocalString('AdminReports', 'labels', 'name')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant='h4'
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {getLocalString('AdminReports', 'labels', 'url')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant='h4'
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {getLocalString('AdminReports', 'labels', 'visible')}
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {reports.map((report, index) => (
                    <TableRow key={index}>
                      {!report.isDeleted ? (
                        <>
                          <StyledTableCell width='15%'>
                            <TextField
                              hiddenLabel
                              placeholder={getLocalString('labels', 'input')}
                              onChange={(e, val) =>
                                handleChangeInput(e, 'name', index)
                              }
                              value={report.name}
                              variant='filled'
                            />
                          </StyledTableCell>
                          <StyledTableCell width='60%'>
                            <TextField
                              hiddenLabel
                              value={report.url}
                              placeholder={getLocalString('labels', 'input')}
                              error={isUrlValid(report.url)}
                              helperText={
                                isUrlValid(report.url)
                                  ? getLocalString('labels', 'incorrectUrl')
                                  : ''
                              }
                              onChange={(e, val) =>
                                handleChangeInput(e, 'url', index)
                              }
                              fullWidth
                              variant='filled'
                            />
                          </StyledTableCell>
                          <StyledTableCell width='23%'>
                            <StyledAutocompleteRolesList
                              multiple
                              options={report.roles}
                              value={report.roles}
                              disableCloseOnSelect
                              disableClearable
                              onChange={(e, val) =>
                                handleChangeSelect(e, val, index)
                              }
                              clearIcon={null}
                              getOptionLabel={option => option.roleName}
                              renderOption={(props, option, { selected }) => (
                                <MenuItem
                                  {...props}
                                  key={option.roleId}
                                  value={option.roleId}
                                >
                                  <Checkbox
                                    icon=<CheckboxIcon
                                      width='12'
                                      height='12'
                                      viewBox='0 0 12 12'
                                    />
                                    checkedIcon=<CheckboxIconChecked
                                      width='12'
                                      height='12'
                                      viewBox='0 0 12 12'
                                    />
                                    style={{ marginRight: 8 }}
                                    value={option.roleId}
                                    checked={option.isChecked}
                                  />
                                  {option.roleName}
                                </MenuItem>
                              )}
                              style={{ width: 500 }}
                              renderInput={params => {
                                let selectedReports = report.roles.filter(
                                  filter => filter.isChecked,
                                );
                                let selectedReportsNames = selectedReports
                                  .map(role => role.roleName)
                                  .join(', ');
                                if (report.isForAll) {
                                  selectedReportsNames = '';
                                }
                                params.InputProps.startAdornment = '';
                                return (
                                  <StyledFlexBox>
                                    {report.isForAll ? (
                                      <StyledAllBox>
                                        {getLocalString('labels', 'allUsers')}
                                      </StyledAllBox>
                                    ) : (
                                      <StyledAllBox>
                                        {selectedReportsNames}
                                      </StyledAllBox>
                                    )}
                                    <StyledTxtField
                                      placeholder={getLocalString(
                                        'labels',
                                        'select',
                                      )}
                                      {...params}
                                      value={selectedReportsNames}
                                    />
                                  </StyledFlexBox>
                                );
                              }}
                              slots={{
                                paper: (e, val) => PaperComponent(e, index),

                                popper: props => (
                                  <ClickAwayListener
                                    onClickAway={e => closePoper(e, index)}
                                  >
                                    <StyledCustomPopper
                                      {...props}
                                      placement='bottom'
                                    ></StyledCustomPopper>
                                  </ClickAwayListener>
                                ),
                              }}
                              slotProps={{
                                chip: {
                                  component: StyledSubItemChip,
                                  deleteIcon: null,
                                },
                              }}
                            />
                          </StyledTableCell>
                          <TableCell width='2%'>
                            <IconButton
                              disableRipple
                              onClick={e => deleteReport(report, index)}
                            >
                              <ReportDeleteIcon
                                width={9.33}
                                height={12}
                                viewBox='0 0 9.33 12'
                              />
                            </IconButton>
                          </TableCell>
                        </>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <IconButton disableRipple onClick={addReport}>
              <PlusIcon width={24} height={24} viewBox='0 0 24 24' />
            </IconButton>
          </StyledSettingsWrap>
        ) : (
          <Grid
            container
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100vh',
            }}
          >
            <CircularProgress color='secondary' />
          </Grid>
        )}
      </Grid>
    </Page>
  );
};
export default ProjectReports;
